/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import Div from "@jumbo/shared/Div";
import Loader from "@jumbo/components/Loader";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TimelineIcon from "@mui/icons-material/Timeline";
import Tooltip from "@mui/material/Tooltip";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import OrderTracking from "./order-tracking/index";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { markModuleRead, fetchUnreadCount } from "app/redux/slice/notificationSlice";
import { useSnackbar } from "notistack";
import { userType } from "app/helpers/userType";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import {
  addPurchaseOrder,
  OFFICE_PRE_BOOK_ORDERS_ADD,
  officePrebookOrderEdit,
} from "app/routes/constant";
import {
  approvePrebookOrder,
  deletePrebookOrder,
  getPrebookOrderList,
  rejectPrebookOrder,
} from "app/redux/slice/prebookOrderSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import { getPrebookApprovers } from "app/redux/slice/prebookOrderSlice";
import Span from "@jumbo/shared/Span";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";

const APPROVAL_STATUS_COLORS = {
  Approved: "success",
  "Pending Approval": "warning",
  Rejected: "error",
};

const OrderPreBook = () => {
  const navigate = useNavigate();
  const { stageAction } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();
  const [isPOCreated, setIsPOCreated] = useState("Pending");
  const [approvalStatus, setApprovalStatus] = useState("All");
  const [rejectReason, setRejectReason] = useState("");
  // Row whose tracking timeline is open, null when the dialog is closed.
  const [trackingRow, setTrackingRow] = useState(null);

  const preBookOrderData = useSelector((state) => state?.prebookOrder);
  const supplierData = useSelector((state) => state?.supplier);

  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [supplierId, setSupplierId] = useState();
  // "Approved By" filter: the chosen approver, and the options for it.
  const [approvedBy, setApprovedBy] = useState(null);
  const [approverOptions, setApproverOptions] = useState([]);

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const {
    isOpen: isGeneratePo,
    onClose: closeGeneratePo,
    actionProps: generatePoActionProps,
    stageAction: generatePoStageAction,
  } = useDisclosure();

  const {
    isOpen: isApprove,
    onClose: closeApprove,
    actionProps: approveActionProps,
    stageAction: approveStageAction,
  } = useDisclosure();

  const {
    isOpen: isReject,
    onClose: closeReject,
    actionProps: rejectActionProps,
    stageAction: rejectStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "orderPreBook" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    generatePoPermission: false,
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        generatePoPermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        generatePoPermission: false,
        deletePermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "create-pre-book") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-pre-book") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-pre-book") {
            permissionValue.updatePermission = true;
          }
          if (num?.name === "generate-purchase-order") {
            permissionValue.generatePoPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    {
      id: "preOrderNumber",
      label: "Order No.",
      minWidth: 100,
      sort: true,
      // Null on the pre-orders raised before numbering existed.
      render: (row) => row?.preOrderNumber || "-",
    },
    {
      id: "date",
      label: "Order date",
      minWidth: 90,
      sort: true,
      render: (row) => moment(row?.date).format("DD-MM-YYYY"),
    },
    {
      id: "vendor",
      label: "Vendor",
      sort: true,
      render: (row) => row?.supplier?.name,
    },
    { id: "totalQuantity", label: "Total Qty", sort: true },
    {
      id: "approvalStatus",
      label: "Approval",
      minWidth: 130,
      // Approval only applies to Supplier-placed (App) pre-orders; CRM ones
      // are active from the moment they are created.
      render: (row) =>
        row?.createdFrom === "APP" ? (
          <Chip
            size="small"
            label={row.approvalStatus || "Pending Approval"}
            color={APPROVAL_STATUS_COLORS[row.approvalStatus] || "warning"}
          />
        ) : (
          "-"
        ),
    },
    {
      id: "approvedBy",
      label: "Approved By",
      minWidth: 140,
      // Only Supplier-placed (App) pre-orders go through approval, so a CRM
      // one has nobody to name; an App one still awaiting a decision has
      // nobody yet either.
      render: (row) => row?.approvedBy?.name || "-",
    },
    {
      id: "isPOCreated",
      label: "PO Generated Status",
      render: (row) =>
        row?.isPOCreated === false ? (
          <Span style={{ color: "red" }}>Pending</Span>
        ) : (
          <Span style={{ color: "green" }}>Generated</Span>
        ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 140,
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              stageAction(row);
              navigate(officePrebookOrderEdit(row?._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <Tooltip title="Order tracking">
            <IconButton onClick={() => setTrackingRow(row)}>
              <TimelineIcon />
            </IconButton>
          </Tooltip>
          {row?.createdFrom === "APP" &&
            row?.approvalStatus !== "Approved" && (
              <Tooltip title="Approve">
                <IconButton
                  disabled={!permission.updatePermission}
                  onClick={() => approveStageAction(row)}
                >
                  <CheckCircleIcon color="success" />
                </IconButton>
              </Tooltip>
            )}
          {row?.createdFrom === "APP" &&
            row?.approvalStatus !== "Rejected" &&
            row?.isPOCreated === false && (
              <Tooltip title="Reject">
                <IconButton
                  disabled={!permission.updatePermission}
                  onClick={() => rejectStageAction(row)}
                >
                  <CancelIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          <IconButton
            // A Supplier-placed pre-order must be approved before it can
            // become a PO - converting is what commits stock.
            disabled={
              !permission.generatePoPermission ||
              row?.isPOCreated !== false ||
              (row?.createdFrom === "APP" &&
                row?.approvalStatus !== "Approved")
            }
            onClick={() => {
              generatePoStageAction(row);
            }}
          >
            <ReceiptIcon />
          </IconButton>
          {permission.deletePermission && (
            <IconButton
              disabled={!permission.deletePermission}
              onClick={() => {
                deleteStageAction(row);
              }}
            >
              <DeleteIcon color={"error"} />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const DeleteProduct = () => {
    dispatch(deletePrebookOrder(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const getData = () => {
    localStorage.setItem("pre-boon-order-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(supplierId && { supplierId: supplierId._id }),
      ...(isPOCreated !== "All" && {
        isPOCreated: isPOCreated === "Pending" ? false : true,
      }),
      ...(approvalStatus !== "All" && { approvalStatus }),
      ...(approvedBy && { approvedBy: approvedBy._id }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getPrebookOrderList(value));
    // Mark as read whenever list is fetched
    dispatch(markModuleRead("Pre Book")).then(() => {
      dispatch(fetchUnreadCount("Pre Book"));
    });
  };

  const notifyAndRefresh = (response) => {
    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) getData();
    return isSuccess;
  };

  const ApprovePreOrder = async () => {
    const response = await dispatch(
      approvePrebookOrder(approveActionProps?._id)
    ).unwrap();
    notifyAndRefresh(response);
    closeApprove();
  };

  const RejectPreOrder = async () => {
    const response = await dispatch(
      rejectPrebookOrder({
        id: rejectActionProps?._id,
        reason: rejectReason.trim(),
      })
    ).unwrap();
    notifyAndRefresh(response);
    setRejectReason("");
    closeReject();
  };

  const GeneratePo = () => {
    closeGeneratePo();
    navigate(addPurchaseOrder(generatePoActionProps?._id));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0,0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy, approvalStatus]);

  useEffect(() => {
    getPermission();
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Office" }));
    dispatch(getPrebookApprovers())
      .unwrap()
      .then((response) => {
        if (response?.status === 200) {
          setApproverOptions(response?.data?.data?.approverList ?? []);
        }
      });
  }, []);

  return (
    <>
      {preBookOrderData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                {/* Widths must add up to 12 or the Submit button wraps onto a
                    second line: Search 2 + Vendor 2 + PO Status 2 + Approval 2
                    + Approved By 2 + Submit 2. */}
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <JumboAutoComplete
                      options={supplierData?.list || []}
                      label="Vendor"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`supplierId`}
                      value={supplierId}
                      onChange={(evt, value) => setSupplierId(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel>Po Generate Status</InputLabel>
                      <Select
                        name="isPOCreated"
                        id="isPOCreated"
                        value={isPOCreated}
                        label="Po Generate Status"
                        size="small"
                        onChange={(e) => setIsPOCreated(e.target.value)}
                      >
                        <MenuItem></MenuItem>
                        {["All", "Generated", "Pending"].map((val) => {
                          return <MenuItem value={val}>{val}</MenuItem>;
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                      <InputLabel>Approval</InputLabel>
                      <Select
                        name="approvalStatus"
                        id="approvalStatus"
                        value={approvalStatus}
                        label="Approval"
                        size="small"
                        onChange={(e) => setApprovalStatus(e.target.value)}
                      >
                        {[
                          "All",
                          "Pending Approval",
                          "Approved",
                          "Rejected",
                        ].map((val) => (
                          <MenuItem key={val} value={val}>
                            {val}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <JumboAutoComplete
                      options={approverOptions}
                      label="Approved By"
                      size="small"
                      getOptionLabel={(option) => option?.name || ""}
                      name="approvedBy"
                      value={approvedBy}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      onChange={(evt, value) => setApprovedBy(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0,0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Order Pre-Book List
                </Typography>
                {permission.createPermission && (
                  <LoadingButton
                    onClick={() => {
                      navigate(OFFICE_PRE_BOOK_ORDERS_ADD);
                    }}
                    sx={{
                      mt: { xs: 1, md: 0 },
                    }}
                    type="submit"
                    variant="contained"
                  >
                    Add Pre-Book Order
                  </LoadingButton>
                )}
              </Stack>
              <Table
                columns={columns}
                rows={preBookOrderData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={preBookOrderData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isGeneratePo}
            onClose={closeGeneratePo}
            title="Are you sure you want to generate PO?"
            onConfirm={GeneratePo}
            btnLabels={{ cancel: "Cancel", confirm: "Generate" }}
          />
          <DialogConfirm
            isOpen={isApprove}
            onClose={closeApprove}
            title="Approve this pre-order?"
            onConfirm={ApprovePreOrder}
            btnLabels={{ cancel: "Cancel", confirm: "Approve" }}
          />
          <DialogDefault
            open={isReject}
            onClose={() => {
              setRejectReason("");
              closeReject();
            }}
            title="Reject this pre-order?"
            fullWidth
            maxWidth="xs"
            content={
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Reason (optional)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setRejectReason("");
                      closeReject();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={RejectPreOrder}
                  >
                    Reject
                  </Button>
                </Stack>
              </Stack>
            }
            actions={<></>}
          />
          <DialogDefault
            open={Boolean(trackingRow)}
            onClose={() => setTrackingRow(null)}
            title={`Order Tracking${
              trackingRow?.preOrderNumber
                ? ` — ${trackingRow.preOrderNumber}`
                : ""
            }`}
            fullWidth
            maxWidth="sm"
            content={
              trackingRow ? (
                <OrderTracking preOrderId={trackingRow._id} />
              ) : null
            }
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the pre-book order?"
            onConfirm={DeleteProduct}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default OrderPreBook;
